import styled from '@emotion/styled';
import { EventTypes } from '@racemap/utilities/consts/events';
import { Card, Col, Flex, Row, Typography } from 'antd';
import type { FC } from 'react';
import { useCurrentEvent } from 'src/lib/customHooks/useCurrentEvent';
import { useCurrentParentEvent } from 'src/lib/customHooks/useCurrentParentEvent';
import { useEventFieldGenerator } from 'src/lib/customHooks/useEventFieldGenerator';
import { EventLogo } from '../../BasicComponents/EventLogo';
import { ImageDropView } from '../../BasicComponents/ImageDropView';

const { Title, Text } = Typography;

export const EventImagePanel: FC = () => {
  const event = useCurrentEvent();
  const parent = useCurrentParentEvent();
  const fieldGenerator = useEventFieldGenerator(event);
  if (event == null || fieldGenerator == null) return <></>;

  // No own logo but the parent group has one → the `/logo` endpoint serves the parent's, so
  // flag the inheritance. When the parent has no logo either the endpoint serves the default
  // fallback, which is not an inheritance and shows no hint.
  const inheritsLogo = event.images?.app == null && parent?.images?.app != null;
  const groupLabel = event.type === EventTypes.STAGE ? 'stage group' : 'contest group';
  const childLabel = event.type === EventTypes.STAGE ? 'stage' : 'contest';

  return (
    <Card>
      <Row justify="space-between" align="top" gutter={16} wrap={false}>
        <Col flex="auto">
          <Title level={3} style={{ marginTop: 0 }}>
            {event.images?.app != null ? 'Event logo' : 'Upload event logo'}
          </Title>
          {inheritsLogo && (
            <Flex align="center" gap={12} style={{ marginTop: 12 }}>
              <EventLogo event={event} size={60} rounded />
              <Text type="secondary" style={{ fontSize: 12 }}>
                No logo set — this event inherits the logo from its {groupLabel}. Upload one here to
                override it just for this {childLabel}.
              </Text>
            </Flex>
          )}
        </Col>
        <Col flex="none">
          <ImageDropContainer className="upload-button">
            <ImageDropView
              imageClass="logo"
              id="racemap_event_image_input"
              withOutPlaceholder={false}
              positionButton="inside-bottom-right"
              {...fieldGenerator('images')}
            />
          </ImageDropContainer>
        </Col>
      </Row>
    </Card>
  );
};

const ImageDropContainer = styled.div`
  padding-top: 6px;
  font-size: 12px;
`;
